import { IconPlus, IconUser } from '@tabler/icons-react'
import { useState } from 'react'

import { ShowOnlyAvailableWaitingListToggle, WaitingListCallout } from '~/app/pages/SurgeonScheduler/shared'
import { AstraButton, AstraDialog, AstraHeading, AstraIcon, AstraTabs, TabOption } from '~/astra'
import { useFlag } from '~/hooks/useFlag'
import { useStore } from '~/store/store'
import { Practitioner, usePractitioner } from '~/storm'
import { getPractitionerFullname } from '~/utils/dips'

import { useIsPredictedWaitingListEnabled } from '../../usePractitionerPredictedWaitingList'
import { CurrentWaitingListPopoverContent } from './CurrentWaitingListPopoverContent'
import { PopoverContentWrapper } from './PopoverContentWrapper'
import { PredictedWaitingListPopoverContent } from './PredictedWaitingListPopoverContent'
import { RecurringAssignmentPopoverContent, RecurringAssignmentPractitionerCreationDialog } from './RecurringAssignmentPopoverContent'

export type TabValue = 'currentWaitingList' | 'predictedWaitingList' | 'recurringAssignmentCreation' | 'recurringAssignmentList'

type Props = {
    practitioner: Practitioner
    onClose: () => void
    defaultTab?: TabValue
}

export const PractitionerPopover = ({ practitioner, onClose, defaultTab = 'currentWaitingList' }: Props) => {
    const [activeTab, setActiveTab] = useState<TabValue>(defaultTab)

    const selectedDate = useStore(state => state.appFilters.selectedDate)
    const recurringAssignmentsEnabled = useFlag('surgeon_scheduler__recurring_assignments')

    const predictedWaitingListEnabledResult = useIsPredictedWaitingListEnabled()

    const practitionerWithDipsResource = usePractitioner({ where: { id: practitioner.id }, include: { dipsResource: true } })
    const practitionerFullname = getPractitionerFullname(practitionerWithDipsResource)

    const tabOptions: TabOption<TabValue>[] = [
        {
            label: 'Venteliste',
            value: 'currentWaitingList',
            content: (
                <PopoverContentWrapper>
                    <div className="flex justify-between pb-3.5">
                        <AstraHeading as="h3" className="px-3 py-2 font-medium text-default">
                            Nåværende venteliste
                        </AstraHeading>
                        <ShowOnlyAvailableWaitingListToggle />
                    </div>
                    <CurrentWaitingListPopoverContent practitioner={practitioner} />
                </PopoverContentWrapper>
            ),
        },
        {
            label: 'Predikert venteliste',
            value: 'predictedWaitingList',
            content: (
                <div className="pb-4 pt-2">
                    {predictedWaitingListEnabledResult.enabled ? (
                        <PredictedWaitingListPopoverContent practitioner={practitioner} date={selectedDate} />
                    ) : (
                        <div className="pt-4">
                            <WaitingListCallout type={predictedWaitingListEnabledResult.reason} />
                        </div>
                    )}
                </div>
            ),
        },
        ...(recurringAssignmentsEnabled
            ? [{ label: 'Regler', value: 'recurringAssignmentList', content: <RecurringAssignmentPopoverContent practitioner={practitioner} /> } as const]
            : []),
    ]

    // recurring assignment creation render its own dialog
    if (activeTab === 'recurringAssignmentCreation') {
        return <RecurringAssignmentPractitionerCreationDialog onBackClick={() => setActiveTab('recurringAssignmentList')} practitioner={practitioner} />
    }

    return (
        <AstraDialog
            align="top"
            title={practitionerFullname}
            isOpen
            onCloseDialog={onClose}
            header={
                <div className="flex items-center gap-2 pl-2">
                    <AstraIcon icon={IconUser} />
                    <AstraHeading as="h3" weight="regular">
                        {practitionerFullname}
                    </AstraHeading>
                </div>
            }
            footer={
                activeTab === 'recurringAssignmentList' ? (
                    <div className="flex w-full justify-end gap-2">
                        <AstraButton
                            label="Ny regel"
                            icon={<AstraIcon icon={IconPlus} />}
                            variant="primary"
                            onClick={() => setActiveTab('recurringAssignmentCreation')}
                        />
                    </div>
                ) : null
            }
        >
            <div className="align-items-center flex w-[580px] flex-col gap-y-2 overflow-auto p-1">
                <AstraTabs<TabValue> options={tabOptions} value={activeTab} onValueChange={setActiveTab} noPadding noSeparatorLine />
            </div>
        </AstraDialog>
    )
}
